import { MembersResponse } from 'module/api/member/types';
import { User, UserStatus, UserType } from 'module/api/user/types';
import { useQuery } from '@tanstack/react-query';
import { UseQueryOptions } from '@tanstack/react-query';
import { MemberAPI } from 'api/member';

const allMembersQueryKey = ['members'];

export const useGetAllMembersQuery = (
  options?: UseQueryOptions<
    MembersResponse,
    Error,
    MembersResponse,
    typeof allMembersQueryKey
  >,
) => {
  return useQuery({
    queryKey: allMembersQueryKey,
    queryFn: () => MemberAPI.getAllMembers(),
    ...options,
  });
};

export const useNativeMembersQuery = (
  options?: Omit<
    UseQueryOptions<
      MembersResponse,
      Error,
      MembersResponse,
      typeof allMembersQueryKey
    >,
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  return useQuery({
    queryKey: allMembersQueryKey,
    queryFn: () => MemberAPI.getAllMembers(),
    select: ({ users, members, teams }) => {
      const result: MembersResponse = {
        teams,
        users: (users ?? []).filter(
          ({ userType }) => userType === UserType.Native,
        ),
        members: (members ?? []).filter(
          ({ user }) => user.userType === UserType.Native,
        ),
      };

      return result;
    },
    ...options,
  });
};

const allUsersQueryKey = ['users'];

export const useGetAllUsersQuery = (
  options?: Omit<
    UseQueryOptions<User[], Error, User[], typeof allMembersQueryKey>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: allUsersQueryKey,
    queryFn: () =>
      MemberAPI.getAllMembers().then((response) =>
        response.members.map(({ user }) => user),
      ),
    ...options,
  });
};
export const useGetAllActiveExternalUsersQuery = (
  options?: Omit<
    UseQueryOptions<User[], Error, User[], typeof allMembersQueryKey>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useGetAllUsersQuery({
    ...options,
    select: (users) =>
      users.filter(
        (user) =>
          user.userType === UserType.External &&
          user.userStatus !== UserStatus.Inactive,
      ),
  });
};
export const useGetAllActiveNativeUsersQuery = (
  options?: Omit<
    UseQueryOptions<User[], Error, User[], typeof allMembersQueryKey>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useGetAllUsersQuery({
    ...options,
    select: (users) =>
      users.filter(
        (user) =>
          user.userType === UserType.Native &&
          user.userStatus !== UserStatus.Inactive,
      ),
  });
};
